import { useCallback, useMemo, useRef, useState } from 'react';
import { useDebounce, useSetState, useUpdateEffect } from 'react-use';
import isEqual from 'lodash/isEqual';
import omit from 'lodash/omit';
import scrollIntoView from 'scroll-into-view-if-needed';

import useEvent from '@lyearn/core/hooks/useEvent';
import { generateShortId } from '@lyearn/core/utils/id';
import { useTranslation } from '@lyearn/i18n';
import { useSnackbar } from '@lyearn/ui';
import useFormKey from '@/components/ContentEditor/hooks/useFormKey';
import { onPublishType } from '@/components/EditorBuilder/Settings/types';
import useNavigation from '@/hooks/useNavigation';
import { useUserContext } from '@/modules/auth/hooks/useUser';
import config from '@/modules/content/config';
import useIsPublishable from '@/modules/content/hooks/useIsPublishable';
import { useEmbeddedSurveyEditor } from '@/modules/corporate/modules/course/components/SurveyCardContainer/components/SurveyEditorModal/context';
import {
  addDatabaseField,
  duplicateDatabaseField,
  hideDatabaseField,
  removeDatabaseField,
  reorderDatabaseViewFields,
  updateDatabaseField,
} from '@/modules/corporate/modules/database/utils/fieldMethods';
import getFields from '@/modules/corporate/modules/database/utils/getFields';
import { DatabaseFormEditorActionEnum } from '@/modules/corporate/modules/database/views/FormView/components/DatabaseFormEditor/types';
import { adaptFormEditorFields } from '@/modules/corporate/modules/database/views/FormView/utils';
import { WELCOME_BLOCK_ID } from '@/modules/corporate/modules/survey/views/SurveyEditor/types';
import { REQUIRED_TAG_ERROR_CODE } from '@/modules/corporate/modules/tags/utils';
import { getUpdatedSubWorkspaceIds } from '@/modules/corporate/utils/subWorkspaces';
import routes from '@/pages/corporate/routes';
import {
  EntityTagsInput,
  PublishContentConfig,
  ResourceStatusEnum,
  ResourceTypeEnum,
  ResourceVersionTypeEnum,
  UpdateSurveyInput,
} from '@/types/schema';
import { getErrorCode } from '@/utils/api/response';

import { SurveyEditorSidebarTabEnum } from '../../components/SurveyEditorSidebar/types';
import useSurveyEditorData from '../useSurveyEditorData';
import useSurveyEditorMutations from '../useSurveyEditorMutations';
import {
  onSurveyEditorAction,
  SurveyDatabaseFieldsState,
  SurveyEditorActionEnum,
  SurveyEditorUIState,
} from './types';
import {
  adaptFieldsState,
  adaptToUpdateSurveyInput,
  getErrorsToShow,
  validateUpdateSurveyInput,
} from './utils';

interface Params {
  contentId: string;
}

const getSurveyDuration = (fieldsCount: number) => (fieldsCount ? fieldsCount * 15 : 1);

const INITIAL_FIELDS_STATE: SurveyDatabaseFieldsState = { metadataFields: [], viewFields: [] };
const INITIAL_UI_STATE: SurveyEditorUIState = {
  selectedBlockId: WELCOME_BLOCK_ID,
  errors: {},
  isOfSameWorkspace: true,
  showPublishChangesDialog: false,
};

const adaptTags = (tags?: EntityTagsInput | null) => {
  if (!tags) {
    return undefined;
  }
  const adaptedTags = tags.set.map((tag) => omit(tag, 'isEditable'));
  return { set: adaptedTags };
};

const useSurveyEditor = ({ contentId }: Params) => {
  const {
    fetching,
    error,
    dbMetadataId,
    viewId,
    fields: initialFields,
    survey: initialSurvey,
  } = useSurveyEditorData({ contentId });

  const { t } = useTranslation();
  const { showSnackbar } = useSnackbar();
  const navigation = useNavigation();
  const embeddedEditor = useEmbeddedSurveyEditor();
  const updateSurveyRef = useRef(true);
  const ignoreIsDirty = useRef(false);
  const [survey, setSurvey] = useState<UpdateSurveyInput | null>(null);
  const [fieldsState, setFieldsState] = useState<SurveyDatabaseFieldsState>(INITIAL_FIELDS_STATE);
  const [uiState, setUIState] = useSetState<SurveyEditorUIState>(INITIAL_UI_STATE);
  const { isSaving, onUpdateSurvey } = useSurveyEditorMutations();
  const fields = useMemo(() => getFields(fieldsState), [fieldsState]);
  const [formKey, setFormKey] = useState<string>(() => generateShortId());
  const { fetchIsPublishable } = useIsPublishable(survey?.id!);
  const { formKey: resetKey, onRefresh } = useFormKey(initialSurvey);
  const user = useUserContext();
  const allowMinorVersionPublish = initialSurvey?.allowMinorVersionPublish;

  const isPublished = [
    ResourceStatusEnum.Published,
    ResourceStatusEnum.UnpublishedChanges,
  ].includes(initialSurvey?.status!);

  const updatedSubWorkspaceIds = useMemo(() => {
    const updatedSubWorkspaceIds = getUpdatedSubWorkspaceIds(
      initialSurvey?.subWorkspaces,
      user.currentSubWorkspace,
    );
    return updatedSubWorkspaceIds;
  }, [user.currentSubWorkspace, initialSurvey?.subWorkspaces]);

  const getUpdateSurveyFields = useCallback(
    (databaseFields: SurveyDatabaseFieldsState) => {
      if (!dbMetadataId || !viewId) {
        return null;
      }

      return adaptFieldsState({ dbMetadataId, viewId, fieldsState: databaseFields });
    },
    [dbMetadataId, viewId],
  );

  useUpdateEffect(() => {
    if (initialSurvey && !survey) {
      setSurvey(adaptToUpdateSurveyInput(initialSurvey));
      updateSurveyRef.current = false;
    }
  }, [initialSurvey]);

  useUpdateEffect(() => {
    if (initialSurvey && initialFields) {
      setSurvey(adaptToUpdateSurveyInput(initialSurvey));
      setFieldsState(initialFields);
      setFormKey(generateShortId());
      updateSurveyRef.current = false;
    }
  }, [resetKey]);

  useUpdateEffect(() => {
    if (initialFields && !fieldsState.metadataFields.length) {
      setFieldsState(initialFields);
      updateSurveyRef.current = false;
    }
  }, [initialFields]);

  useUpdateEffect(() => {
    const prevErrors = uiState.errors;

    if (!Object.keys(prevErrors).length || !survey) {
      return;
    }

    const nextErrors = validateUpdateSurveyInput(t, survey.name ?? '', fields);

    setUIState({ errors: getErrorsToShow({ prevErrors, nextErrors }) });
  }, [survey, fieldsState, t]);

  useDebounce(
    () => {
      if (survey && !isSaving && updateSurveyRef.current) {
        if (
          !isEqual(initialFields?.metadataFields, fieldsState.metadataFields) ||
          !isEqual(initialFields?.viewFields, fieldsState.viewFields)
        ) {
          onUpdateSurvey({
            id: contentId,
            subWorkspaceIds: updatedSubWorkspaceIds,
            dbFields: getUpdateSurveyFields(fieldsState),
            duration: getSurveyDuration(fields.length),
          });
        }
      }
    },
    1500,
    [fieldsState],
  );

  useDebounce(
    () => {
      if (survey && !isSaving && updateSurveyRef.current) {
        // @ts-ignore
        const { type, status, ...restSurvey } = survey;
        onUpdateSurvey({
          subWorkspaceIds: updatedSubWorkspaceIds,
          ...restSurvey,
          tags: adaptTags(restSurvey.tags),
        });
      }
    },
    1500,
    [survey],
  );

  const getPreviousFieldId = useCallback(
    (fieldId: string) => {
      const fieldIndex = fields.findIndex((field) => field.metadataField.fieldId === fieldId);
      const previousFieldId = fields[fieldIndex - 1]?.metadataField.fieldId;

      return previousFieldId || WELCOME_BLOCK_ID;
    },
    [fields],
  );

  const handleTagsError = useCallback(
    (id: string) => {
      setUIState((prevState) => ({
        ...prevState,
        isPublishing: false,
      }));
      ignoreIsDirty.current = true;
      navigation.replace(routes.SurveyEditor(id), {
        query: {
          validationError: 'tag',
          tab: SurveyEditorSidebarTabEnum.SurveySettings,
        },
        appendQuery: false,
      });
    },
    [navigation, setUIState],
  );

  const onAction: onSurveyEditorAction = useEvent(async (action) => {
    switch (action.type) {
      case DatabaseFormEditorActionEnum.SelectField: {
        setUIState({ selectedBlockId: action.payload.fieldId || WELCOME_BLOCK_ID });
        break;
      }
      case SurveyEditorActionEnum.UpdateSurvey: {
        setSurvey((prevSurvey) => ({ ...prevSurvey!, ...action.payload.updates }));
        updateSurveyRef.current = true;
        break;
      }
      case SurveyEditorActionEnum.ExecuteUpdateSurvey: {
        setSurvey((prevSurvey) => ({ ...prevSurvey!, ...action.payload.updates }));
        const res = await onUpdateSurvey({
          id: survey?.id!,
          subWorkspaceIds: updatedSubWorkspaceIds,
          cover: survey?.cover,
          ...action.payload.updates,
        });
        if (!res.response.data?.updateSurvey.survey) {
          if (action.payload.snackbarProps?.message) {
            showSnackbar({
              message: action.payload.snackbarProps?.message,
            });
          }
          setUIState((prevState) => {
            return {
              ...prevState,
              isOfSameWorkspace: false,
            };
          });
        }

        break;
      }
      case SurveyEditorActionEnum.PublishSurvey: {
        const errors = validateUpdateSurveyInput(t, survey?.name ?? '', fields);

        if (Object.keys(errors).length) {
          setUIState({ errors });
          setTimeout(() => {
            const errorBlock = document.querySelector('[data-database-form-error]');
            if (errorBlock) {
              scrollIntoView(errorBlock, { behavior: 'smooth', block: 'start' });
            }
          }, 0);
        } else {
          setUIState((prevState) => ({
            ...prevState,
            isPublishing: true,
          }));
          const result = await fetchIsPublishable();
          const publishable = result?.publishable;
          if (publishable) {
            if (!publishable.isPublishable && publishable.reason === REQUIRED_TAG_ERROR_CODE) {
              handleTagsError(survey?.id!);
              return;
            }
          }
          const nextSurvey = { ...survey!, status: ResourceStatusEnum.Published };
          setSurvey(nextSurvey);
          // @ts-ignore
          const { type, ...restSurvey } = nextSurvey;
          onUpdateSurvey({
            subWorkspaceIds: updatedSubWorkspaceIds,
            ...restSurvey,
            tags: adaptTags(restSurvey.tags),
            duration: getSurveyDuration(fields.length),
            dbFields: getUpdateSurveyFields(fieldsState),
            publishConfig: action.payload.publishConfig,
          }).then(({ response }) => {
            if (response.error) {
              const errorCode = getErrorCode(response.error);
              if (errorCode === REQUIRED_TAG_ERROR_CODE) {
                handleTagsError(nextSurvey.id);
                return;
              }
            } else {
              setUIState((prevState) => ({
                ...prevState,
                isPublishing: false,
              }));
              ignoreIsDirty.current = true;
              const surveyId = response.data?.updateSurvey.survey?.id;
              if (surveyId) {
                if (action.payload.snackbarProps?.message) {
                  showSnackbar({
                    message: action.payload.snackbarProps?.message,
                  });
                }
                if (embeddedEditor) {
                  embeddedEditor.onClose();
                } else {
                  navigation.replace(routes.AdminSurveyDetails(surveyId));
                }
              }
            }
          });
        }
        break;
      }
      case DatabaseFormEditorActionEnum.ReorderFields: {
        const { fromIndex, toIndex } = action.payload;
        setFieldsState(({ metadataFields, viewFields }) =>
          reorderDatabaseViewFields({ metadataFields, viewFields, fromIndex, toIndex }),
        );
        updateSurveyRef.current = true;
        break;
      }
      case DatabaseFormEditorActionEnum.DuplicateField: {
        const { fieldId } = action.payload;
        const { newFieldId, ...nextState } = duplicateDatabaseField({ ...fieldsState, fieldId });
        setFieldsState(nextState);
        newFieldId && setUIState({ selectedBlockId: newFieldId });
        updateSurveyRef.current = true;
        break;
      }
      case SurveyEditorActionEnum.AddField: {
        const { type } = action.payload;
        const { fieldId, ...nextState } = addDatabaseField({ ...fieldsState, type });
        setFieldsState(nextState);
        setUIState({ selectedBlockId: fieldId });
        updateSurveyRef.current = true;
        break;
      }
      case DatabaseFormEditorActionEnum.RemoveField: {
        const { fieldId } = action.payload;

        setUIState((prevState) => {
          if (prevState.selectedBlockId === fieldId) {
            return { selectedBlockId: getPreviousFieldId(fieldId) };
          }
          return prevState;
        });
        setFieldsState((prevState) => removeDatabaseField({ ...prevState, fieldId }));
        updateSurveyRef.current = true;
        break;
      }
      case DatabaseFormEditorActionEnum.HideField: {
        const { fieldId } = action.payload;

        setUIState((prevState) => {
          if (prevState.selectedBlockId === fieldId) {
            return { selectedBlockId: getPreviousFieldId(fieldId) };
          }
          return prevState;
        });
        setFieldsState((prevState) => hideDatabaseField({ ...prevState, fieldId }));
        updateSurveyRef.current = true;
        break;
      }
      case DatabaseFormEditorActionEnum.UpdateField: {
        const { fieldId, updates } = action.payload;
        setFieldsState((prevState) => updateDatabaseField({ ...prevState, fieldId, updates }));
        updateSurveyRef.current = true;
        break;
      }
      case SurveyEditorActionEnum.PublishDialog: {
        setUIState((prevState) => ({ ...prevState, showPublishChangesDialog: true }));
        break;
      }

      default: {
        break;
      }
    }
  });

  const isFieldPublished = useCallback(
    (fieldId: string) => {
      if (!isPublished) {
        return false;
      }

      return Boolean(initialFields?.metadataFields.find((field) => field.fieldId === fieldId));
    },
    [initialFields?.metadataFields, isPublished],
  );

  const onClosePublishChangesDialog = useCallback(() => {
    setUIState((prevState) => ({ ...prevState, showPublishChangesDialog: false }));
  }, [setUIState]);

  const onPublish = useCallback(() => {
    const contentType = t(config[ResourceTypeEnum.Survey].label.title);

    if (initialSurvey?.status === ResourceStatusEnum.Draft) {
      onAction({
        type: SurveyEditorActionEnum.PublishSurvey,
        payload: {
          publishConfig: { versionType: ResourceVersionTypeEnum.Major },
          snackbarProps: {
            message: t('Common:Form.ContentTypePublishedSuccessfully', { contentType }),
          },
        },
      });
    } else {
      onAction({
        type: SurveyEditorActionEnum.PublishDialog,
      });
    }
  }, [initialSurvey?.status, onAction, t]);

  const onPublishSubmit: onPublishType = useCallback(
    (publishConfig: PublishContentConfig) => {
      onAction({
        type: SurveyEditorActionEnum.PublishSurvey,
        payload: {
          publishConfig,
          snackbarProps: {
            message:
              publishConfig.versionType === ResourceVersionTypeEnum.Major
                ? t('Common:Form.ChangesPublishedSuccessfully')
                : t('Common:Form.UpdatedExistingVersionSuccessfully'),
          },
        },
      });
      return new Promise(() => {});
    },
    [onAction, t],
  );

  const adaptedFields = useMemo(
    () => adaptFormEditorFields({ isFieldPublished, fields, t, isEditor: true }),
    [isFieldPublished, fields, t],
  );

  const getFieldAttributes = useCallback(
    (fieldId: string) => {
      const databaseField = fields.find(({ viewField }) => viewField.fieldId === fieldId)!;
      return {
        index: databaseField.index,
        descriptionEnabled: Boolean(databaseField.viewField?.descriptionEnabled),
      };
    },
    [fields],
  );

  return {
    error,
    fetching,
    isFieldPublished,
    fields,
    adaptedFields,
    getFieldAttributes,
    isPublished,
    isSaving,
    uiState,
    survey,
    onAction,
    initialSurvey,
    formKey,
    onClosePublishChangesDialog,
    onPublishSubmit,
    onPublish,
    onRevertSuccess: onRefresh,
    allowMinorVersionPublish,
  };
};

export default useSurveyEditor;
